import React, { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const GeometricShapes3D = () => {
  const { isDark } = useTheme();
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let shapes = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Base shape class
    class GeometricShape {
      constructor(type) {
        this.type = type;
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.z = Math.random() * 1000 + 500;
        this.size = Math.random() * 40 + 20;
        this.speedX = (Math.random() - 0.5) * 0.6;
        this.speedY = (Math.random() - 0.5) * 0.6;
        this.speedZ = Math.random() * 1.2 + 0.3;
        this.rotation = Math.random() * 360;
        this.rotationSpeed = (Math.random() - 0.5) * 2;
        this.opacity = Math.random() * 0.5 + 0.3;
        
        // Purple color palette
        const colors = [
          { r: 147, g: 51, b: 234, name: 'purple-600' },
          { r: 168, g: 85, b: 247, name: 'purple-500' },
          { r: 192, g: 132, b: 252, name: 'purple-400' },
          { r: 59, g: 130, b: 246, name: 'blue-500' },
          { r: 37, g: 99, b: 235, name: 'blue-600' },
        ];
        this.color = colors[Math.floor(Math.random() * colors.length)];
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        this.z -= this.speedZ;
        this.rotation += this.rotationSpeed;

        if (this.z <= 0 || this.x < -200 || this.x > canvas.width + 200 || 
            this.y < -200 || this.y > canvas.height + 200) {
          this.x = Math.random() * canvas.width;
          this.y = Math.random() * canvas.height;
          this.z = 1500;
        }
      }

      draw() {
        const scale = 1000 / (this.z + 1000);
        const x = this.x * scale + canvas.width / 2 * (1 - scale);
        const y = this.y * scale + canvas.height / 2 * (1 - scale);
        const size = this.size * scale;

        if (size < 2) return;

        ctx.save();
        ctx.translate(x, y);
        ctx.rotate((this.rotation * Math.PI) / 180);

        const alpha = this.opacity * scale;
        ctx.fillStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${alpha})`;
        ctx.strokeStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${alpha * 1.5})`;
        ctx.lineWidth = Math.max(1, scale * 1.5);

        switch (this.type) {
          case 'square':
            this.drawSquare(size);
            break;
          case 'triangle':
            this.drawTriangle(size);
            break;
          case 'parallelogram':
            this.drawParallelogram(size);
            break;
          case 'pentagon':
            this.drawPentagon(size);
            break;
          case 'octagon':
            this.drawOctagon(size);
            break;
          case 'hexagon':
            this.drawHexagon(size);
            break;
        }

        ctx.restore();
      }

      drawSquare(size) {
        const s = size / 2;
        ctx.beginPath();
        ctx.rect(-s, -s, size, size);
        ctx.fill();
        ctx.stroke();
        
        // Inner square for 3D effect
        const innerSize = size * 0.7;
        const is = innerSize / 2;
        ctx.strokeStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.opacity * 0.3})`;
        ctx.lineWidth = 1;
        ctx.strokeRect(-is, -is, innerSize, innerSize);
      }

      drawTriangle(size) {
        const height = size * Math.sqrt(3) / 2;
        ctx.beginPath();
        ctx.moveTo(0, -height / 2);
        ctx.lineTo(-size / 2, height / 2);
        ctx.lineTo(size / 2, height / 2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        
        // Inner triangle
        const innerSize = size * 0.6;
        const innerHeight = innerSize * Math.sqrt(3) / 2;
        ctx.beginPath();
        ctx.moveTo(0, -innerHeight / 2);
        ctx.lineTo(-innerSize / 2, innerHeight / 2);
        ctx.lineTo(innerSize / 2, innerHeight / 2);
        ctx.closePath();
        ctx.strokeStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.opacity * 0.3})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      drawParallelogram(size) {
        const offset = size * 0.3;
        ctx.beginPath();
        ctx.moveTo(-size / 2 + offset, -size / 2);
        ctx.lineTo(size / 2 + offset, -size / 2);
        ctx.lineTo(size / 2 - offset, size / 2);
        ctx.lineTo(-size / 2 - offset, size / 2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        
        // Inner lines for 3D effect
        ctx.strokeStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.opacity * 0.4})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, -size / 2);
        ctx.lineTo(0, size / 2);
        ctx.stroke();
      }

      drawPentagon(size) {
        this.drawPolygon(size, 5);
      }

      drawOctagon(size) {
        this.drawPolygon(size, 8);
      }

      drawHexagon(size) {
        this.drawPolygon(size, 6);
      }

      drawPolygon(size, sides) {
        const radius = size / 2;
        ctx.beginPath();
        for (let i = 0; i <= sides; i++) {
          const angle = (i * 2 * Math.PI) / sides - Math.PI / 2;
          const x = radius * Math.cos(angle);
          const y = radius * Math.sin(angle);
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        
        // Inner polygon
        const innerRadius = radius * 0.6;
        ctx.beginPath();
        for (let i = 0; i <= sides; i++) {
          const angle = (i * 2 * Math.PI) / sides - Math.PI / 2;
          const x = innerRadius * Math.cos(angle);
          const y = innerRadius * Math.sin(angle);
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.opacity * 0.3})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    // Create shapes with different types
    const shapeTypes = ['square', 'triangle', 'parallelogram', 'pentagon', 'octagon', 'hexagon'];
    const shapeCount = 30;
    
    for (let i = 0; i < shapeCount; i++) {
      const type = shapeTypes[i % shapeTypes.length];
      shapes.push(new GeometricShape(type));
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      shapes.forEach(shape => {
        shape.update();
        shape.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDark]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-[1]"
      style={{ opacity: isDark ? 0.35 : 0.25 }}
    />
  );
};

export default GeometricShapes3D;
